// ============================================================
// HARMONIC CHARTER — Core Types
// ============================================================

// ------------------------------------------------------------
// PRIMITIVES
// ------------------------------------------------------------

export type NoteRoot =
  | 'C' | 'C#' | 'Db'
  | 'D' | 'D#' | 'Eb'
  | 'E'
  | 'F' | 'F#' | 'Gb'
  | 'G' | 'G#' | 'Ab'
  | 'A' | 'A#' | 'Bb'
  | 'B';

export type Scale = 'major' | 'minor';

export interface Key {
  root: NoteRoot;
  scale: Scale;
}

export interface TimeSignature {
  numerator: number;
  denominator: number;
}

// ------------------------------------------------------------
// CHORD
// ------------------------------------------------------------

export type Degree = 1 | 2 | 3 | 4 | 5 | 6 | 7;

// Input form — accepted by parsers (both symbol and word form)
export type Accidental = '#' | 'b' | 'sharp' | 'flat' | null;

// Normalized form — stored on Chord after parsing, used by formatter/voicing/playback
export type NormalizedAccidental = 'sharp' | 'flat' | null;

export type ChordQuality =
  | 'maj' | 'min' | 'dom7' | 'maj7' | 'min7'
  | 'dim' | 'dim7' | 'hdim7' | 'aug'
  | 'sus2' | 'sus4' | 'min6' | 'maj6'
  | '9' | 'maj9' | 'min9' | 'add9'
  | '11' | '13'
  | 'power'
  // Word-form aliases — keyDetection.ts, playbackEngine.ts, formatter.ts
  | 'major' | 'minor'
  | 'dominant7' | 'dominantSeventh'
  | 'major7' | 'majorSeventh'
  | 'minor7' | 'minorSeventh'
  | 'diminished' | 'diminished7'
  | 'halfDiminished' | 'halfDiminished7' | 'halfDiminishedSeventh'
  | 'augmented'
  | 'minorMajor7' | 'minorMajorSeventh'
  | 'dominant9' | 'dominantNinth'
  | 'major9' | 'majorNinth'
  | 'minor9' | 'minorNinth'
  | 'suspended2' | 'suspended4';

export type ChordExtension =
  | 'b9' | '#9' | '#11' | 'b13'
  | 'add9' | 'add11' | 'add13'
  | '6' | '69';

/** Alias used by formatter.ts and parsers */
export type Extension = ChordExtension;

/** Slash bass note — degree with optional accidental, e.g. '/3', '/b7' */
export type SlashBass = string | null;

export interface Chord {
  id?: string;
  degree: Degree;
  accidental: NormalizedAccidental;
  quality: ChordQuality;
  extensions: ChordExtension[];
  slash: string | null;
  /** Beat position within the bar (0-based). */
  position: number;
  borrowed: boolean;
  /**
   * If true, this slot is a rhythm placeholder with no assigned chord.
   * Renders nothing — user fills it in via chord input.
   */
  isEmpty?: boolean;
  raw?: string;
  duration?: number;
}

// ------------------------------------------------------------
// RHYTHM / NOTE VALUE TYPES
// ------------------------------------------------------------

export type NoteValue =
  | 'whole'
  | 'half'       | 'dottedHalf'
  | 'quarter'    | 'dottedQuarter' | 'tripletQuarter'
  | 'eighth'     | 'dottedEighth'  | 'tripletEighth' | 'eighth-triplet'
  | 'sixteenth'
  | 'half-triplet'
  | 'quarter-triplet';

export interface RhythmSlot {
  id: string;
  beats: number;
  noteValue: NoteValue;
  chord?: Chord | null;   // optional so partial slot objects are valid
  position: number;
}

/** Legacy alias */
export type Slot = RhythmSlot;

/**
 * Returns the beat duration for a given NoteValue.
 */
export function noteValueBeats(nv: NoteValue): number {
  switch (nv) {
    case 'whole':           return 4;
    case 'half':            return 2;
    case 'dottedHalf':      return 3;
    case 'quarter':         return 1;
    case 'dottedQuarter':   return 1.5;
    case 'tripletQuarter':
    case 'quarter-triplet': return 2 / 3;
    case 'eighth':          return 0.5;
    case 'dottedEighth':    return 0.75;
    case 'tripletEighth':
    case 'eighth-triplet':  return 1 / 3;
    case 'half-triplet':    return 4 / 3;
    case 'sixteenth':       return 0.25;
    default:                return 1;
  }
}

/**
 * Returns a human-readable label for a NoteValue (e.g. "♩", "♪.", "⅓").
 */
export function noteValueLabel(nv: NoteValue): string {
  switch (nv) {
    case 'whole':           return '𝅝';
    case 'half':            return '𝅗𝅥';
    case 'dottedHalf':      return '𝅗𝅥.';
    case 'quarter':         return '♩';
    case 'dottedQuarter':   return '♩.';
    case 'tripletQuarter':
    case 'quarter-triplet': return '♩₃';
    case 'eighth':          return '♪';
    case 'dottedEighth':    return '♪.';
    case 'tripletEighth':
    case 'eighth-triplet':  return '♪₃';
    case 'half-triplet':    return '𝅗𝅥₃';
    case 'sixteenth':       return '𝅘𝅥𝅯';
    default:                return '♩';
  }
}

/**
 * Returns a short keyboard shortcut key string for a NoteValue.
 * Used by RhythmInputPanel for keyboard input.
 */
export function noteValueKey(nv: NoteValue): string {
  switch (nv) {
    case 'whole':           return '1';
    case 'dottedHalf':      return '2';
    case 'half':            return '3';
    case 'dottedQuarter':   return '4';
    case 'quarter':         return '5';
    case 'tripletQuarter':
    case 'quarter-triplet': return '6';
    case 'dottedEighth':    return '7';
    case 'eighth':          return '8';
    case 'tripletEighth':
    case 'eighth-triplet':  return '9';
    case 'sixteenth':       return '0';
    case 'half-triplet':    return 'h';
    default:                return '';
  }
}

// ------------------------------------------------------------
// BAR
// ------------------------------------------------------------

export interface Bar {
  id: string;
  chords: Chord[];
  timeSignature: TimeSignature | null;
  repeatStart: boolean;
  repeatEnd: number | null;
  eighthNotes: boolean;
  hasCustomRhythm: boolean;
  note: string | null;
  lyrics: string | null;
}

// ------------------------------------------------------------
// SECTION
// ------------------------------------------------------------

export interface Section {
  id: string;
  title: string;
  key: Key | null;
  bars: Bar[];
  barsPerRow: number | null;
  note: string | null;
}

// ------------------------------------------------------------
// ANNOTATION — free-floating, song-level
// ------------------------------------------------------------

export interface AnnotationStyle {
  color: string;
  fontSize: number;
  italic: boolean;
  bold: boolean;
}

export interface Annotation {
  id: string;
  text: string;
  /** Fraction [0..1] of total chart width — survives layout changes. */
  xRatio: number;
  /** Pixels from top of chart overlay layer. */
  yOffset: number;
  style: AnnotationStyle;
}

export const DEFAULT_ANNOTATION_STYLE: AnnotationStyle = {
  color: '#e63946',
  fontSize: 13,
  italic: true,
  bold: false,
};

// ------------------------------------------------------------
// DISPLAY MODE
// ------------------------------------------------------------

export type DisplayMode = 'nashville' | 'roman' | 'chords';

// ------------------------------------------------------------
// HARMONIC ANALYSIS
// ------------------------------------------------------------

export type HarmonicFunction =
  | 'T' | 'S' | 'D'
  | 'BD' | 'BT' | 'BS'
  | 'SD' | 'SS'
  | '?';

export interface ChordAnalysis {
  function: HarmonicFunction;
  borrowed: boolean;
  isSecondary: boolean;
  secondaryTarget: number | null;
  label: string;
}

export interface AnalysisSettings {
  enabled: boolean;
  showFunction: boolean;
  showBorrowed: boolean;
  showDegree: boolean;
  showSecondary: boolean;
}

// ------------------------------------------------------------
// SYNC STATUS
// ------------------------------------------------------------

export type SyncStatus = 'idle' | 'syncing' | 'error' | 'offline';

// ------------------------------------------------------------
// SONG
// ------------------------------------------------------------

export interface Song {
  id: string;
  title: string;
  defaultKey: Key;
  timeSignature: TimeSignature;
  displayMode: DisplayMode;
  analysisSettings: AnalysisSettings;
  tempo: string | null;
  feel: string | null;
  note: string | null;
  barsPerRow: number;
  sections: Section[];
  /** Free-floating annotations — independent of bars/sections. */
  annotations: Annotation[];
  createdAt: string;
  updatedAt: string;
}

// ------------------------------------------------------------
// PROJECT FILE FORMAT
// ------------------------------------------------------------

export interface HChartFile {
  version: string;
  song: Song;
}

// ------------------------------------------------------------
// PARSER TYPES
// ------------------------------------------------------------

export type InputGrammar = 'nashville' | 'roman' | 'chordName';

export type ParseResult =
  | { ok: true; chord: Chord }
  | { ok: false; error: string; raw: string };

export type BarParseResult =
  | { ok: true; chords: Chord[] }
  | { ok: false; errors: Array<{ token: string; error: string }> };

// ------------------------------------------------------------
// EDITOR STATE TYPES
// ------------------------------------------------------------

export interface EditorSelection {
  sectionId: string;
  barId: string;
}

export interface EditorState {
  song: Song;
  selection: EditorSelection | null;
  isDirty: boolean;
}